import { clsx, type ClassValue } from "clsx";
import { v4 } from "uuid";
import { twMerge } from "tailwind-merge";
import {
	ProfileEntityType,
} from "../../../lib/supabase/supabaseTypes";
import { supabase } from "../../../lib/supabase/supabaseClient";
import { Logger } from "../../../lib/logger/Logger";
import { showNotification } from "../store/Central/selectors";
import { uploadFileWithProvidedPath } from "../../../dentlab/src/hooks/useStorage";
import { createFileMetadata } from "@/lib/utils/file-utils";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function getExtension(fileName: string) {
	return fileName.split(".")?.[1];
}

export const attachFile = async (
	image: File,
	profile: ProfileEntityType,
	jobId: number
) => {
	if (!profile) {
		Logger.error("Profile not found");
		showNotification({
			message: "Profile not found",
			type: "error",
		});
		return;
	}

	const date = new Date()
		.toLocaleDateString("de-DE", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		})
		.replace(/\./g, "-");

	const extension = getExtension(image.name);

	// Get existing files for today to determine next number
	const { data: existingFiles } = await supabase
		.from("files")
		.select("file_name")
		.eq("organization_id", profile.organization_id)
		.like("file_name", `${date}-%`);

	// Find highest number
	let maxNumber = 0;
	existingFiles?.forEach((file) => {
		const match = file.file_name?.match(/-(\d{3})\./);
		if (match) {
			const num = parseInt(match[1]);
			maxNumber = Math.max(maxNumber, num);
		}
	});

	// Create new filename with incremented number
	const newNumber = (maxNumber + 1).toString().padStart(3, "0");
	const newFileName = `${date}-${newNumber}.${extension}`;

	const path = `${v4()}-${newFileName}`;

	const { success, pathName } = await uploadFileWithProvidedPath({
		filePath: path,
		fileBody: image,
		fileOptions: {
			contentType: image.type,
			metadata: {
				originalName: newFileName,
				size: image.size,
				lastModified: image.lastModified,
			},
		},
	});

	if (!success || !pathName) {
		Logger.error("Error uploading file");
		showNotification({
			message: "Fehler beim Hochladen der Datei",
			type: "error",
		});
		return;
	}

	const { error: fileError } = await supabase.from("files").insert({
		file_name: newFileName,
		bucket_name: "v1",
		path_name: pathName,
		job_id: jobId,
		organization_id: profile?.organization_id,
		meta_data: createFileMetadata(image),
	});

	if (fileError) {
		Logger.error(fileError, {}, "Error uploading file");
		showNotification({
			message: "Fehler beim Hochladen der Datei",
			type: "error",
		});
		return;
	}

	showNotification({
		message: "Bild erfolgreich angehängt",
		type: "success",
	});
};
